/**
 * aging-csv.ts — client-side CSV export for the AR aging report (Phase 12 / Task 12.4).
 *
 * Pure string builder + a small browser download helper, mirroring web-dispatch's
 * gps-export.ts convention (the codebase has no server-side text/csv endpoint). The
 * exported data is exactly what GET /v1/billing/aging returned — which is already
 * VIEW_INVOICES-gated — so the CSV is gated the same way as the on-screen report.
 *
 * Amounts are emitted as plain dollar decimals (cents/100, 2dp) for spreadsheet use —
 * exact, since cents are integers (no float drift).
 */

import type { AgingOperator, AgingTotals } from '../features/billing/hooks';

/** RFC-4180 cell quoting: wrap + double any cell containing a comma, quote, or newline. */
function csvCell(v: string | number | null): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Integer cents → a plain dollar decimal string (no symbol), e.g. 25000 → "250.00". */
function dollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Build the aging CSV: one row per operator + a trailing TOTAL row. */
export function agingToCsv(operators: readonly AgingOperator[], totals: AgingTotals): string {
  const header = 'Operator,Current,31-60,61-90,90+,Total';
  const rows = operators.map((o) =>
    [
      csvCell(o.operatorName ?? o.operatorId ?? 'Unmapped operator'),
      dollars(o.current),
      dollars(o.d31_60),
      dollars(o.d61_90),
      dollars(o.d90plus),
      dollars(o.total),
    ].join(','),
  );
  const totalRow = [
    'TOTAL',
    dollars(totals.current),
    dollars(totals.d31_60),
    dollars(totals.d61_90),
    dollars(totals.d90plus),
    dollars(totals.total),
  ].join(',');
  return [header, ...rows, totalRow].join('\n');
}

/** Trigger a browser download of `content` as `filename`. */
export function downloadFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
